import { SdkContext } from '../internal'
import { noteRowFrom, versionedRowWrite } from '@/lib/realtime/rowVersion'
import { listItems } from '@/lib/utils'
import type {
  CreditBalance,
  CreditEntriesPage,
  CreditEntry,
  CreditEntryKind,
  CreditGrantInput,
  CreditSettings,
  CreditSettingsInput
} from '@/types/credits'

const toPage = (result: unknown): CreditEntriesPage => {
  const page = result as { pagination?: CreditEntriesPage['pagination'] } | null
  return { items: listItems<CreditEntry>(result), pagination: page?.pagination ?? null }
}

/** F-79: the credits ledger. Paths are written by hand (F-03); the community is the header, never the path. */
export class CreditsResource {
  constructor(private ctx: SdkContext) {}

  async mine(): Promise<CreditBalance> {
    return this.ctx.call({ httpMethod: 'GET', path: '/credits/me' }, {})
  }

  async myEntries(page = 1, limit = 20, kind?: CreditEntryKind): Promise<CreditEntriesPage> {
    return toPage(await this.ctx.raw({ httpMethod: 'GET', path: '/credits/me/entries' }, { query: { page, limit, kind } }))
  }

  async balanceOf(memberId: string): Promise<CreditBalance> {
    return this.ctx.call({ httpMethod: 'GET', path: '/credits/members/{memberId}' }, { memberId })
  }

  async entriesOf(memberId: string, page = 1, limit = 20, kind?: CreditEntryKind): Promise<CreditEntriesPage> {
    return toPage(
      await this.ctx.raw({ httpMethod: 'GET', path: '/credits/members/{memberId}/entries' }, { memberId, query: { page, limit, kind } })
    )
  }

  /** F-93 1B: a company's wallet. Owner, admin, finance and that company's admin. */
  async companyBalance(companyId: string): Promise<CreditBalance> {
    return this.ctx.call({ httpMethod: 'GET', path: '/credits/companies/{companyId}' }, { companyId })
  }

  async companyEntries(companyId: string, page = 1, limit = 20, kind?: CreditEntryKind): Promise<CreditEntriesPage> {
    return toPage(
      await this.ctx.raw({ httpMethod: 'GET', path: '/credits/companies/{companyId}/entries' }, { companyId, query: { page, limit, kind } })
    )
  }

  async grant(input: CreditGrantInput): Promise<CreditBalance & { entry: CreditEntry }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/credits/grants', hasBody: true }, { body: input })
  }

  async adjust(input: CreditGrantInput): Promise<CreditBalance & { entries: CreditEntry[] }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/credits/adjustments', hasBody: true }, { body: input })
  }

  async getSettings(): Promise<CreditSettings> {
    return noteRowFrom('creditSettings', await this.ctx.call<CreditSettings>({ httpMethod: 'GET', path: '/credits/settings' }, {}))
  }

  /** Sends the version read (`updatedAt`); a stale save (409) asks before saving over it. */
  async updateSettings(input: CreditSettingsInput): Promise<CreditSettings> {
    return versionedRowWrite('creditSettings', (v) => this.ctx.call<CreditSettings>({ httpMethod: 'PUT', path: '/credits/settings', hasBody: true }, { body: { ...input, ...(v ? { updatedAt: v } : {}) } }))
  }
}
